import { useLocation } from 'react-router-dom';
import { useWorkspaceMode } from '@/hooks/auth';

export type PortalMode = 'talent' | 'company' | 'recruiter' | 'internal' | 'nanny' | 'mother';

/**
 * Unified hook for determining the current portal mode based on URL path
 * Used by both ModeSelector and UnifiedSidebar to ensure consistency
 */
export function useCurrentPortalMode(): PortalMode {
  const location = useLocation();
  const { currentMode: userSelectedMode, setCurrentMode } = useWorkspaceMode();
  
  const path = location.pathname;
  
  // Direct workspace routes - highest priority (these override stored mode)
  if (path.includes('/workspace/nanny/')) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    if (storedMode !== 'nanny') {
      setCurrentMode('nanny');
    }
    return 'nanny';
  }
  if (path.includes('/workspace/mother/')) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    if (storedMode !== 'mother') {
      setCurrentMode('mother');
    }
    return 'mother';
  }
  if (path.includes('/workspace/internal/')) {
    // Sync localStorage with URL for workspace routes
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    if (storedMode !== 'internal') {
      setCurrentMode('internal');
    }
    return 'internal';
  }
  if (path.includes('/workspace/company/')) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    if (storedMode !== 'company') {
      setCurrentMode('company');
    }
    return 'company';
  }
  if (path.includes('/workspace/recruiter/')) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    if (storedMode !== 'recruiter') {
      setCurrentMode('recruiter');
    }
    return 'recruiter';
  }
  if (path.includes('/workspace/talent/')) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    if (storedMode !== 'talent') {
      setCurrentMode('talent');
    }
    return 'talent';
  }
  
  // For NON-workspace routes, ONLY READ from localStorage - never write
  const nonWorkspaceRoutes = ['/chat', '/calendar', '/notifications', '/evee-ai', '/evee-ai-agent', '/help', '/account', '/user', '/apply'];
  const isNonWorkspaceRoute = nonWorkspaceRoutes.some(route => path.startsWith(route));
  
  if (isNonWorkspaceRoute) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    return storedMode || 'talent';
  }
  
  // For unknown routes under /workspace, use stored mode or default to talent
  if (path.startsWith('/workspace/')) {
    const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
    return storedMode || 'talent';
  }
  
  // All other routes: show stored mode to avoid unexpected jumps
  const storedMode = localStorage.getItem('workspace_mode') as PortalMode;
  return storedMode || 'talent';
}